import * as THREE from 'three';
import {
  isEditorShapeAsset,
  isNestedGlbAssetPivot,
} from '../gridpagefunction/MeshExtractation';
import store from '../../redux/store';
import { cloneMaterialSettingsForDuplicate } from '../gridpagefunction/materialFunction';

/**
 * Helpers for editor shape assets (Cube, Cone, etc.) — publish export and reload.
 */

/** Shapes that are a single mesh on the asset root (not multi-part groups like Capsule). */
export const SINGLE_MESH_SHAPE_CODES = new Set([
  'Plane',
  'Cube',
  'Sphere',
  'Cylinder',
  'Cone',
  'Torus',
  'Rectangle',
  'Ellipse',
]);

export function isSingleMeshShapeCode(meshCode) {
  return meshCode && SINGLE_MESH_SHAPE_CODES.has(meshCode);
}

export function resolveAssetParentRef(parent) {
  if (!parent) return null;
  if (typeof parent === 'object') {
    if (parent?.isObject3D) {
      return (
        parent._id ||
        parent.url ||
        parent.name ||
        parent.userData?.parentAssetId ||
        null
      );
    }
    return (
      parent._id ||
      parent.id ||
      parent.name ||
      parent.temp_file_name ||
      null
    );
  }
  return parent;
}

const fileNameStem = (value) =>
  String(value || '').replace(/\.[^/.]+$/, '');

/** Map published parent id (copy name / temp id) to loaded asset _id after reload. */
export function resolveAssetParentId(parentId, assetsList = []) {
  if (!parentId || !assetsList?.length) return parentId;
  const normalized = String(parentId);
  const normalizedStem = fileNameStem(normalized);

  const found = assetsList.find((item) => {
    if (!item) return false;
    const keys = [
      item._id,
      item.id,
      item.name,
      item.temp_file_name,
      item.url,
      item.file_name,
      item.asset_file_name,
      item.userData?.parentAssetId,
    ].filter(Boolean);

    return keys.some((key) => {
      const keyStr = String(key);
      return (
        keyStr === normalized ||
        fileNameStem(keyStr) === normalizedStem ||
        keyStr === normalizedStem
      );
    });
  });

  if (found) {
    return found._id || found.id || found.temp_file_name || parentId;
  }
  return parentId;
}

/** Other shape/mesh records parented under this asset in API data. */
export function getChildShapeAssetsFromMeshData(meshData, parentMesh) {
  if (!Array.isArray(meshData) || !parentMesh) return [];
  const parentId = parentMesh._id || parentMesh.id;
  const parentName = parentMesh.name;

  return meshData.filter((entry) => {
    if (!entry || entry._id === parentMesh._id) return false;
    const ref = resolveAssetParentRef(entry.parent);
    if (!ref) return false;
    if (parentId && ref === parentId) return true;
    if (parentName && ref === parentName) return true;
    return false;
  });
}

function disposeMeshObject(meshObj) {
  if (!meshObj) return;
  if (meshObj.geometry) meshObj.geometry.dispose();
  const mats = Array.isArray(meshObj.material)
    ? meshObj.material
    : [meshObj.material];
  mats.forEach((m) => {
    if (!m) return;
    Object.keys(m).forEach((key) => {
      const value = m[key];
      if (value?.isTexture) value.dispose();
    });
    m.dispose?.();
  });
}

/**
 * Detach an object from its parent even when parent.children was frozen
 * (e.g. after clone/load) and Three.js remove() cannot splice the array.
 */
function safeDetachFromParent(child) {
  if (!child?.parent) return;

  const parent = child.parent;

  if (typeof child.removeFromParent === 'function') {
    try {
      child.removeFromParent();
      return;
    } catch {
      // Fall through to manual detach
    }
  }

  try {
    parent.remove(child);
    return;
  } catch {
    // Frozen / sealed children array — replace with a mutable copy
  }

  const index = parent.children.indexOf(child);
  if (index === -1) return;

  parent.children = parent.children.filter((entry) => entry !== child);
  child.parent = null;
}

function safeAddToParent(parent, child) {
  if (!parent || !child) return;
  if (child.parent === parent) return;

  if (child.parent) {
    safeDetachFromParent(child);
  }

  try {
    parent.add(child);
    return;
  } catch {
    parent.children = [...(parent.children || []), child];
    child.parent = parent;
  }
}

function removeMeshFromParent(meshObj) {
  if (!meshObj?.parent) return;
  safeDetachFromParent(meshObj);
  disposeMeshObject(meshObj);
}

/**
 * Parent shape GLB files published before nested-export fix may bake child geometry.
 * Remove meshes that belong to child shape assets so only the parent shape remains.
 */
export function removeBakedChildShapesFromLoadedModel(
  model,
  parentMeshCode,
  childAssets
) {
  if (!model || !childAssets?.length) return;

  const childCodes = childAssets
    .map((m) => String(m.mesh_code || m.shape_type || '').toLowerCase())
    .filter(Boolean);
  if (!childCodes.length) return;

  const parentCode = String(parentMeshCode || '').toLowerCase();
  const meshes = [];
  model.traverse((o) => {
    if (o.isMesh) meshes.push(o);
  });

  if (meshes.length <= 1) return;

  meshes.forEach((meshObj) => {
    const label = `${meshObj.name || ''} ${meshObj.parent?.name || ''}`.toLowerCase();
    const matchesChild = childCodes.some((code) => code && label.includes(code));
    const matchesParent = parentCode && label.includes(parentCode);
    if (matchesChild && !matchesParent) {
      removeMeshFromParent(meshObj);
    }
  });

  const remaining = [];
  model.traverse((o) => {
    if (o.isMesh) remaining.push(o);
  });

  if (remaining.length <= 1) return;

  if (isSingleMeshShapeCode(parentMeshCode)) {
    let keep = remaining[0];
    if (parentCode) {
      const named = remaining.find((m) =>
        String(m.name || '').toLowerCase().includes(parentCode)
      );
      if (named) keep = named;
    }
    remaining.forEach((m) => {
      if (m !== keep) removeMeshFromParent(m);
    });
  }
}

function getHostAssetIds(host) {
  if (!host) return [];
  return [
    host._id,
    host.id,
    host.name,
    host.url,
    host.temp_file_name,
    host.file_name,
    host.asset_file_name,
    host.userData?.file_name,
  ].filter(Boolean);
}

/** Resolve a mesh asset record to its scene object. */
export function resolveSceneObjectByAsset(scene, entry) {
  if (!scene || !entry) return null;
  const ids = [
    entry._id,
    entry.id,
    entry.name,
    entry.url,
    entry.temp_file_name,
  ].filter(Boolean);

  for (const id of ids) {
    const direct = scene.getObjectByName(id);
    if (direct) return direct;
  }

  let found = null;
  scene.traverse((obj) => {
    if (found) return;
    if (
      ids.some(
        (id) => obj._id === id || obj.name === id || obj.url === id
      )
    ) {
      found = obj;
    }
  });
  return found;
}

/**
 * Child mesh/shape assets under this host — Three.js children and Redux parent links.
 * Left panel can show Redux-only children that are not yet in host.children.
 */
export function getNestedSceneMeshChildren(host, scene = null) {
  if (!host) return [];

  const seen = new Set();
  const result = [];
  const add = (obj) => {
    if (!obj || obj === host || seen.has(obj.uuid)) return;
    if (!isNestedGlbAssetPivot(obj, host)) return;
    seen.add(obj.uuid);
    result.push(obj);
  };

  (host.children || []).forEach((child) => add(child));

  if (scene) {
    const hostIds = getHostAssetIds(host);
    const assets = store.getState().assets_object?.assets_data?.mesh || [];
    assets.forEach((entry) => {
      const parentRef =
        resolveAssetParentRef(entry.parent) ||
        entry.userData?.parentAssetId ||
        entry.parentAssetId;
      if (!parentRef || !hostIds.includes(parentRef)) return;
      const obj = resolveSceneObjectByAsset(scene, entry);
      if (obj) add(obj);
    });
  }

  return result;
}

/** Ensure Redux-linked children are attached under host in the Three.js graph. */
export function syncNestedChildrenSceneParent(host, nestedChildren) {
  if (!host || !nestedChildren?.length) return;
  nestedChildren.forEach((child) => {
    if (child.parent === host) return;
    try {
      host.attach(child);
    } catch {
      safeAddToParent(host, child);
    }
  });
}

/** Save local transform relative to parent (used before detach / duplicate). */
export function captureChildLocalTransform(child, parent) {
  if (!child) return null;
  if (parent && child.parent === parent) {
    return {
      position: child.position.clone(),
      rotation: child.rotation.clone(),
      scale: child.scale.clone(),
    };
  }
  if (!parent) {
    return {
      position: child.position.clone(),
      rotation: child.rotation.clone(),
      scale: child.scale.clone(),
    };
  }

  parent.updateMatrixWorld(true);
  child.updateMatrixWorld(true);

  const localPos = new THREE.Vector3();
  parent.worldToLocal(
    localPos.copy(child.getWorldPosition(new THREE.Vector3()))
  );

  const parentQuat = new THREE.Quaternion();
  const childQuat = new THREE.Quaternion();
  parent.getWorldQuaternion(parentQuat);
  child.getWorldQuaternion(childQuat);
  const localQuat = parentQuat.clone().invert().multiply(childQuat);
  const localRot = new THREE.Euler().setFromQuaternion(
    localQuat,
    child.rotation.order
  );

  const childWorldScale = new THREE.Vector3();
  const parentWorldScale = new THREE.Vector3();
  child.getWorldScale(childWorldScale);
  parent.getWorldScale(parentWorldScale);

  return {
    position: localPos,
    rotation: localRot,
    scale: new THREE.Vector3(
      childWorldScale.x / (parentWorldScale.x || 1),
      childWorldScale.y / (parentWorldScale.y || 1),
      childWorldScale.z / (parentWorldScale.z || 1)
    ),
  };
}

function applyLocalTransformToChild(child, localTransform) {
  if (!child || !localTransform) return;
  child.position.copy(localTransform.position);
  child.rotation.copy(localTransform.rotation);
  child.scale.copy(localTransform.scale);
  child.updateMatrixWorld(true);
}

/** Align Redux parent links with the Three.js scene graph before publish. */
export function syncAllMeshHierarchyFromRedux(scene) {
  if (!scene) return;
  const assets = store.getState().assets_object?.assets_data?.mesh || [];
  const seenHosts = new Set();

  assets.forEach((entry) => {
    const host = resolveSceneObjectByAsset(scene, entry);
    if (!host || seenHosts.has(host.uuid)) return;
    seenHosts.add(host.uuid);
    const nested = getNestedSceneMeshChildren(host, scene);
    syncNestedChildrenSceneParent(host, nested);
  });
}

export function detachNestedShapeAssetsForExport(host, scene = null) {
  const nested = getNestedSceneMeshChildren(host, scene);
  syncNestedChildrenSceneParent(host, nested);

  const savedLocals = nested.map((child) =>
    captureChildLocalTransform(child, host)
  );

  [...nested].forEach((child) => {
    safeDetachFromParent(child);
  });

  return { detached: nested, savedLocals };
}

/** Find Redux mesh record for a scene object. */
export function findMeshItemForSceneObject(sceneObj) {
  if (!sceneObj) return null;
  const assets = store.getState().assets_object?.assets_data?.mesh || [];
  const ids = [
    sceneObj._id,
    sceneObj.name,
    sceneObj.url,
    sceneObj.temp_file_name,
  ].filter(Boolean);

  return (
    assets.find(
      (a) =>
        ids.includes(a._id) ||
        ids.includes(a.id) ||
        ids.includes(a.name) ||
        ids.includes(a.url)
    ) || null
  );
}

export function reattachDetachedShapeAssets(host, detached, savedLocals = null) {
  if (!host || !detached?.length) return;
  detached.forEach((child, index) => {
    if (child.parent === host) return;
    safeAddToParent(host, child);
    applyLocalTransformToChild(
      child,
      savedLocals?.[index] || captureChildLocalTransform(child, host)
    );
  });
}

/** Resolve shape type code (Cube, Pyramid, …) from scene object or Redux item. */
export function resolveShapeMeshCode(source, item) {
  return (
    source?.mesh_code ||
    source?.userData?.mesh_code ||
    item?.mesh_code ||
    item?.shape_type ||
    null
  );
}

/** Copy shape identity onto a duplicate so it stays the same geometry type. */
export function applyShapeMetadataToDuplicate(target, source, item, duplicateFileName) {
  const meshCode = resolveShapeMeshCode(source, item);
  const isShape =
    isEditorShapeAsset(source) ||
    isEditorShapeAsset(item) ||
    source?.glb_type === 'shapes' ||
    item?.glb_type === 'shapes';
  if (!meshCode || !isShape) return false;

  target.mesh_code = meshCode;
  target.glb_type = 'shapes';
  target.isShapes = true;
  target.file_name = duplicateFileName;
  target.asset_file_name = duplicateFileName;

  target.userData = target.userData || {};
  target.userData.glb_type = 'shapes';
  target.userData.mesh_code = meshCode;
  target.userData.isShapes = true;
  target.userData.file_name = duplicateFileName;

  if (source?.userData?.material_settings) {
    target.userData.material_settings = cloneMaterialSettingsForDuplicate(
      source,
      source.userData.material_settings
    );
  }
  return true;
}
